import React, { useId, useMemo } from 'react';
import {
  getColorSwatchSegments,
  lightenHexColor
} from '../../utils/colorUtils';
import {
  CABLE_ICON_OUTLINE,
  COPPER_COLOR,
  COPPER_SECTION_COLOR
} from './cableIconStyles';

const createPolePalette = (primary, secondary = primary) => {
  const sectionPrimary = lightenHexColor(primary);
  const sectionSecondary = lightenHexColor(secondary);
  return {
    primary,
    secondary,
    sectionPrimary,
    sectionSecondary
  };
};

const getPolePalette = (color) => {
  const segments = getColorSwatchSegments(color);
  const primary = segments[0] || '#808080';
  return createPolePalette(primary, segments[1] || primary);
};

const PoleGradient = ({ id, primary, secondary, smooth = false, type, poleIndex }) => (
  <linearGradient
    id={id}
    x1="0"
    y1="0"
    x2="0"
    y2="1"
    data-pole-gradient={type}
    data-gradient-pole-index={poleIndex}
  >
    {smooth ? (
      <>
        <stop offset="0" stopColor={primary} />
        <stop offset="1" stopColor={secondary} />
      </>
    ) : (
      <>
        <stop offset="0" stopColor={primary} />
        <stop offset="0.48" stopColor={primary} />
        <stop offset="0.52" stopColor={secondary} />
        <stop offset="1" stopColor={secondary} />
      </>
    )}
  </linearGradient>
);

const STOCK_PALETTES = [
  createPolePalette('#f33d11', '#ed170c'),
  createPolePalette('#3717d1', '#4665ca'),
  createPolePalette('#FFD600', '#4CAF50')
];

const MultipolarCableIcon = ({ poleColors, poleCount, className = '', style = {}, stock = false }) => {
  const componentId = useId().replace(/[^a-zA-Z0-9_-]/g, '');
  const visiblePoleCount = stock ? 3 : (Number(poleCount) === 2 ? 2 : 3);
  const palettes = useMemo(
    () => stock
      ? STOCK_PALETTES
      : Array.from({ length: visiblePoleCount }, (_, index) => getPolePalette(poleColors[index])),
    [poleColors, stock, visiblePoleCount]
  );
  const gradientIds = palettes.map((_, index) => `multipolar-pole-${componentId}-${index}`);
  const sectionGradientIds = palettes.map((_, index) => `multipolar-pole-section-${componentId}-${index}`);

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 590 325"
      preserveAspectRatio="xMidYMid meet"
      aria-hidden="true"
      focusable="false"
      data-cable-icon={stock ? 'multipolar-stock' : 'multipolar-dynamic'}
      data-pole-count={visiblePoleCount}
      className={className}
      style={style}
    >
      <defs>
        {palettes.map((palette, index) => (
          <PoleGradient
            key={gradientIds[index]}
            id={gradientIds[index]}
            primary={palette.primary}
            secondary={palette.secondary}
            smooth={stock && index < 2}
            type="jacket"
            poleIndex={index}
          />
        ))}
        {palettes.map((palette, index) => (
          <PoleGradient
            key={sectionGradientIds[index]}
            id={sectionGradientIds[index]}
            primary={palette.sectionPrimary}
            secondary={palette.sectionSecondary}
            smooth={stock && index < 2}
            type="section"
            poleIndex={index}
          />
        ))}
      </defs>
      <g>
        <path
          data-cable-part="multipolar-jacket"
          fill="var(--multipolar-jacket-color, #404040)"
          stroke={CABLE_ICON_OUTLINE}
          strokeWidth="2.442"
          strokeLinecap="square"
          strokeLinejoin="round"
          d="M80.099 17.443v.348C38.906 22.777 6.345 81.839 6.345 153.895c0 72.056 32.561 131.118 73.754 136.104l191.416-1.506-1.921-272.491L80.099 17.443z"
        />
        <path
          data-cable-part="multipolar-jacket-section"
          fill="var(--multipolar-jacket-section-color, #939195)"
          stroke={CABLE_ICON_OUTLINE}
          strokeWidth="2.532"
          strokeLinecap="square"
          strokeLinejoin="round"
          d="M354.465 150.659c0 105.04-66.164 170.691-119.094 118.171-24.566-24.375-39.699-69.422-39.699-118.171 0-105.041 66.164-170.692 119.095-118.171 24.565 24.374 39.698 69.421 39.698 118.171z"
        />

        <g data-pole-index="0">
          <path
            fill={`url(#${gradientIds[0]})`}
            stroke={CABLE_ICON_OUTLINE}
            strokeWidth="1.5"
            strokeLinecap="square"
            strokeLinejoin="round"
            d="M264.062 180.224c-11.131.708-20.046 19.165-20.046 41.778 0 23.06 9.286 41.787 20.725 41.778 0 0 149.278.164 150.21.164 11.439 0 20.725-18.717 20.725-41.778s-9.286-41.777-20.725-41.777-20.724 18.717-20.724 41.777c0 20.809 7.554 38.089 17.435 41.264-10.199-2.607-18.093-20.163-18.093-41.428 0-21.362 7.97-38.988 18.237-41.47 0 0-147.343-.313-147.476-.308h-.268z"
          />
          <ellipse
            data-pole-section-index="0"
            cx="414.951"
            cy="222.166"
            rx="20.725"
            ry="41.778"
            fill={`url(#${sectionGradientIds[0]})`}
            stroke={CABLE_ICON_OUTLINE}
            strokeWidth="1.5"
          />
          <path
            data-pole-copper-index="0"
            fill={COPPER_COLOR}
            stroke={CABLE_ICON_OUTLINE}
            strokeWidth=".977"
            strokeLinecap="square"
            strokeLinejoin="round"
            d="M417.414 191.472c-8.263.526-14.88 14.227-14.88 31.012 0 17.118 6.893 31.019 15.384 31.012 0 0 110.81.122 111.502.122 8.491 0 15.384-13.894 15.384-31.012s-6.893-31.012-15.384-31.012-15.383 13.894-15.383 31.012c0 15.446 5.607 28.274 12.942 30.631-7.571-1.935-13.431-14.968-13.431-30.753 0-15.857 5.916-28.941 13.537-30.783 0 0-109.373-.233-109.473-.229h-.198z"
          />
          <ellipse
            data-pole-copper-section-index="0"
            cx="529.42"
            cy="222.606"
            rx="15.384"
            ry="31.012"
            fill={COPPER_SECTION_COLOR}
            stroke={CABLE_ICON_OUTLINE}
            strokeWidth=".977"
          />
        </g>

        <g data-pole-index="1">
          <path
            fill={`url(#${gradientIds[1]})`}
            stroke={CABLE_ICON_OUTLINE}
            strokeWidth="1.5"
            strokeLinecap="square"
            strokeLinejoin="round"
            d="M302.393 52.89c-11.132.707-20.046 19.165-20.046 41.777 0 23.061 9.286 41.788 20.725 41.778 0 0 149.278.165 150.21.165 11.439 0 20.725-18.718 20.725-41.778s-9.286-41.778-20.725-41.778-20.724 18.718-20.724 41.778c0 20.808 7.554 38.089 17.435 41.264-10.199-2.607-18.093-20.164-18.093-41.429 0-21.362 7.97-38.988 18.237-41.469 0 0-147.343-.314-147.477-.308h-.267z"
          />
          <ellipse
            data-pole-section-index="1"
            cx="453.282"
            cy="94.832"
            rx="20.725"
            ry="41.778"
            fill={`url(#${sectionGradientIds[1]})`}
            stroke={CABLE_ICON_OUTLINE}
            strokeWidth="1.5"
          />
          <path
            data-pole-copper-index="1"
            fill={COPPER_COLOR}
            stroke={CABLE_ICON_OUTLINE}
            strokeWidth=".977"
            strokeLinecap="square"
            strokeLinejoin="round"
            d="M455.745 64.138c-8.263.525-14.88 14.226-14.88 31.012 0 17.117 6.893 31.019 15.384 31.012 0 0 110.81.122 111.502.122 8.491 0 15.384-13.894 15.384-31.012S576.242 64.26 567.751 64.26s-15.384 13.894-15.384 31.012c0 15.446 5.608 28.274 12.942 30.63-7.571-1.934-13.43-14.967-13.43-30.752 0-15.858 5.916-28.941 13.537-30.783 0 0-109.374-.233-109.473-.229h-.198z"
          />
          <ellipse
            data-pole-copper-section-index="1"
            cx="567.751"
            cy="95.272"
            rx="15.384"
            ry="31.012"
            fill={COPPER_SECTION_COLOR}
            stroke={CABLE_ICON_OUTLINE}
            strokeWidth=".977"
          />
        </g>

        {visiblePoleCount === 3 && (
          <g data-pole-index="2">
            <path
              fill={`url(#${gradientIds[2]})`}
              stroke={CABLE_ICON_OUTLINE}
              strokeWidth="1.5"
              strokeLinecap="square"
              strokeLinejoin="round"
              d="M229.873 103.999c-11.132.708-20.046 19.166-20.046 41.778 0 23.06 9.286 41.787 20.724 41.778 0 0 149.279.164 150.211.164 11.439 0 20.724-18.717 20.724-41.778s-9.285-41.777-20.724-41.777-20.724 18.717-20.724 41.777c0 20.809 7.554 38.089 17.434 41.264-10.199-2.607-18.092-20.163-18.092-41.428 0-21.362 7.97-38.988 18.236-41.469 0 0-147.342-.314-147.476-.309h-.267z"
            />
            <ellipse
              data-pole-section-index="2"
              cx="380.762"
              cy="145.941"
              rx="20.724"
              ry="41.777"
              fill={`url(#${sectionGradientIds[2]})`}
              stroke={CABLE_ICON_OUTLINE}
              strokeWidth="1.5"
            />
            <path
              data-pole-copper-index="2"
              fill={COPPER_COLOR}
              stroke={CABLE_ICON_OUTLINE}
              strokeWidth=".977"
              strokeLinecap="square"
              strokeLinejoin="round"
              d="M383.225 115.247c-8.263.526-14.88 14.227-14.88 31.012 0 17.118 6.892 31.019 15.383 31.012 0 0 110.811.122 111.503.122 8.491 0 15.384-13.894 15.384-31.012s-6.893-31.011-15.384-31.011-15.384 13.894-15.384 31.011c0 15.447 5.608 28.275 12.942 30.631-7.571-1.935-13.43-14.967-13.43-30.753 0-15.857 5.916-28.94 13.537-30.783 0 0-109.374-.232-109.473-.229h-.198z"
            />
            <ellipse
              data-pole-copper-section-index="2"
              cx="495.231"
              cy="146.381"
              rx="15.384"
              ry="31.011"
              fill={COPPER_SECTION_COLOR}
              stroke={CABLE_ICON_OUTLINE}
              strokeWidth=".977"
            />
          </g>
        )}
      </g>
    </svg>
  );
};

export default MultipolarCableIcon;
